import React, { useEffect, useRef, useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Mosque, PhotoUploadData } from '../types/mosque';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalAnimation } from '../hooks/useModalAnimation';
import { usePhotos } from '../hooks/usePhotos';
import { resolvePhotoUploadErrorMessage } from '../utils/photoUploadErrors';
import PhotoUpload from './PhotoUpload';

interface AddPhotoFormProps {
  isOpen: boolean;
  mosque: Mosque | null;
  onClose: () => void;
  onUploaded?: () => void;
}

const COUNTDOWN_SECONDS = 2;

const AddPhotoForm: React.FC<AddPhotoFormProps> = ({ isOpen, mosque, onClose, onUploaded }) => {
  const { t } = useLanguage();
  const { uploadPhotos } = usePhotos();
  const { isVisible, isClosing, handleClose } = useModalAnimation({ isOpen, onClose });
  const [photos, setPhotos] = useState<PhotoUploadData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const photosRef = useRef<PhotoUploadData[]>([]);

  const clearPhotos = () => {
    photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    setPhotos([]);
  };

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    if (isOpen) {
      setSubmitError(null);
      setSubmitSuccess(false);
      setCountdown(COUNTDOWN_SECONDS);
      clearPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    return () => {
      photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    };
  }, []);

  useEffect(() => {
    if (submitSuccess && countdown > 0) {
      const timer = setTimeout(() => setCountdown((value) => value - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (submitSuccess && countdown === 0) {
      setSubmitSuccess(false);
      handleClose();
    }
  }, [submitSuccess, countdown, handleClose]);

  if (!isVisible || !mosque) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!mosque || photos.length === 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await uploadPhotos(mosque.id, photos);
      clearPhotos();
      onUploaded?.();
      setSubmitSuccess(true);
      setCountdown(COUNTDOWN_SECONDS);
    } catch (error) {
      console.error('Failed to upload photos:', error);
      setSubmitError(resolvePhotoUploadErrorMessage(t, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
        <div className="modal-content success-modal" onClick={(event) => event.stopPropagation()}>
          <div className="success-message">
            <CheckCircle size={48} className="success-icon" />
            <h2>{t('photos.uploadSuccessTitle')}</h2>
            <p>{t('photos.uploadSuccessMessage')}</p>
            <p className="countdown-text">{t('common.closingIn')} {countdown}s</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('photos.addToMosque').replace('{name}', mosque.name)}</h2>
          <button className="close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {submitError && (
          <div className="error-banner">
            <span>{submitError}</span>
            <button type="button" onClick={() => setSubmitError(null)}>
              <X size={16} />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mosque-form">
          <div className="form-content">
            <div className="form-group">
              <label>{t('photos.title')}</label>
              <PhotoUpload
                photos={photos}
                onPhotosChange={setPhotos}
                maxPhotos={5}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleClose} className="btn-cancel">
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting || photos.length === 0}>
              {isSubmitting ? `${t('photos.uploading')}...` : t('photos.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPhotoForm;
